import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { Formik } from 'formik';
import Button from '../components/Button';
import { Field, FieldInput, FieldLabel } from '../components/Field';
import Loader from '../components/Loader';
import { Modal, ModalExit, ModalName, ModalPart } from '../components/Modal';
import { useToast } from '../components/Toast';
import { VIEWER } from '../utilities/queries';
import { SEND_EMAIL_CODE, VERIFY_EMAIL_CODE } from '../utilities/mutations';

const EditEmailModal = () => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(true);
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { loading, data } = useQuery(VIEWER);
  const [sendEmailCode] = useMutation(SEND_EMAIL_CODE);
  const [verifyEmailCode] = useMutation(VERIFY_EMAIL_CODE);

  const close = () => navigate('/settings/main');

  return (
    <>
      {isEditModalOpen && (
        <Modal onClose={() => close()}>
          {loading ? (
            <ModalPart>
              <Loader />
            </ModalPart>
          ) : (
            <>
              <ModalExit />
              <ModalName>Edit email</ModalName>
              <Formik
                initialValues={{ email: data.viewer.email }}
                onSubmit={async (values) => {
                  try {
                    await sendEmailCode({
                      variables: { email: values.email },
                    });
                  } catch (error) {
                    toast({ children: error.message });

                    return;
                  }

                  setIsEditModalOpen(false);
                  setIsVerifyModalOpen(true);
                }}
              >
                {({
                  values,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                }) => (
                  <form onSubmit={handleSubmit}>
                    <ModalPart>
                      <Field>
                        <FieldLabel>Email</FieldLabel>
                        <FieldInput
                          type="email"
                          name="email"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.email}
                          data-autofocus
                        />
                      </Field>
                    </ModalPart>
                    <ModalPart>
                      <Button
                        type="submit"
                        disabled={
                          values.email === data.viewer.email || isSubmitting
                        }
                        isPrimary
                        isExtended
                      >
                        Next
                      </Button>
                    </ModalPart>
                  </form>
                )}
              </Formik>
            </>
          )}
        </Modal>
      )}
      {isVerifyModalOpen && (
        <Modal onClose={() => close()}>
          <ModalExit />
          <ModalName>Verify your new email</ModalName>
          <Formik
            initialValues={{ code: '' }}
            onSubmit={async (values) => {
              try {
                await verifyEmailCode({
                  variables: { code: values.code },
                });
              } catch (error) {
                toast({ children: error.message });

                return;
              }

              setIsVerifyModalOpen(false);
              close();
              toast({ children: 'Email updated' });
            }}
          >
            {({
              values,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <ModalPart>
                  <Field>
                    <FieldLabel>Code</FieldLabel>
                    <FieldInput
                      type="text"
                      name="code"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.code}
                      autoFocus
                    />
                  </Field>
                </ModalPart>
                <ModalPart>
                  <Button
                    type="submit"
                    disabled={values.code === '' || isSubmitting}
                    isPrimary
                    isExtended
                  >
                    Next
                  </Button>
                </ModalPart>
              </form>
            )}
          </Formik>
        </Modal>
      )}
    </>
  );
};

export default EditEmailModal;
